import {
  VirtusSidebar,
  type SidebarItem,
  type SidebarLinkRenderProps,
  type SidebarSection,
  type SidebarTooltipRenderProps,
} from "@virtus-automation/platform-ui";
import { LifeBuoy } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "../../components";
import { useActiveContext } from "../../contexts/ActiveContextContext";
import { useTranslation } from "../../contexts/LanguageContext";
import postsService from "../../services/postsService";
import { getVirtusChatBaseUrl } from "../../shared/virtusCrmSso";
import {
  isCurrentPage,
  isNavItemWithChildren,
  navigation,
} from "../config/navigation";

// Grupo cujo contador de falhas aparece (no grupo fechado e no subitem "Postagens")
const POSTS_GROUP = "Posts (Orgânico)";
const POSTS_HREF = "/posts";

const renderLink = ({ href, children, ...props }: SidebarLinkRenderProps) =>
  /^https?:\/\//.test(href) ? (
    <a href={href} {...props}>
      {children}
    </a>
  ) : (
    <Link to={href} {...props}>
      {children}
    </Link>
  );

const renderTooltip = ({
  content,
  children,
  side,
  delay,
  fullWidth,
}: SidebarTooltipRenderProps) => (
  <Tooltip
    content={content}
    position={side}
    delay={delay}
    wrapperClassName={fullWidth ? "w-full" : ""}
    zIndex={10060}
    usePortal
  >
    {children}
  </Tooltip>
);

interface SidebarProps {
  /** Modo recolhido (só ícones). Ignorado no drawer mobile, que é sempre expandido. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

/**
 * Adaptador do SMM para a `VirtusSidebar` do platform-ui: converte `navigation.tsx` em seções e
 * acrescenta o que é só do SMM (contador de falhas, login automático no Virtus CRM, traduções).
 */
const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggleCollapsed,
  isMobile,
  mobileOpen,
  onCloseMobile,
}) => {
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();
  const location = useLocation();

  const isActive = useCallback(
    (href: string) => isCurrentPage(location.pathname, href),
    [location.pathname],
  );

  // ─── Contador de falhas ───────────────────────────────────────────────────
  const [failedCount, setFailedCount] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const stats = await postsService.getStats();
        setFailedCount(stats.failed || 0);
      } catch (err) {
        console.error("Erro ao buscar estatísticas de posts:", err);
      }
    };
    // Uma consulta ao montar, a cada 5 minutos e ao voltar para a aba
    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchStats();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // ─── SSO: abrir Virtus CRM com login automático ───────────────────────────
  const [ssoLoadingHref, setSsoLoadingHref] = useState<string | null>(null);

  const handleSSOExternalLink = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      const crmBaseUrl = getVirtusChatBaseUrl();
      const isCrmLink =
        crmBaseUrl &&
        href.startsWith(crmBaseUrl.split("/sso")[0].split("?")[0]);

      if (!isCrmLink) return; // comportamento padrão para outros links externos

      e.preventDefault();
      if (ssoLoadingHref) return;

      setSsoLoadingHref(href);
      try {
        const token = localStorage.getItem("access_token");
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        if (activeContext) {
          headers["X-Active-Context"] = activeContext.type;
          if (
            activeContext.type === "CLIENT_COMPANY" &&
            activeContext.clientCompany?.id
          ) {
            headers["X-Active-Context-Id"] = String(
              activeContext.clientCompany.id,
            );
          } else if (activeContext.organization?.id) {
            headers["X-Active-Context-Id"] = String(
              activeContext.organization.id,
            );
          }
        }

        const response = await fetch("/api/auth/sso-token", {
          method: "POST",
          headers,
        });

        if (!response.ok) throw new Error("Falha ao gerar token SSO");
        const data = await response.json();

        // Monta a URL de SSO do CRM: <base_crm>/sso?token=...&redirect=/settings?tab=whatsapp
        const crmOrigin = new URL(
          crmBaseUrl.includes("://") ? crmBaseUrl : `http://${crmBaseUrl}`,
        ).origin;
        const settingsUrl =
          import.meta.env.VITE_VIRTUS_CRM_SETTINGS_URL ||
          `${crmOrigin}/dashboard`;
        const redirectPath = settingsUrl.replace(crmOrigin, "") || "/dashboard";
        let ssoUrl = `${crmOrigin}/sso?token=${encodeURIComponent(data.sso_token)}&redirect=${encodeURIComponent(redirectPath)}`;

        if (
          activeContext?.type === "CLIENT_COMPANY" &&
          activeContext?.clientCompany?.id
        ) {
          ssoUrl += `&client=${activeContext.clientCompany.id}`;
        }
        if (activeContext?.organization?.id) {
          ssoUrl += `&org=${activeContext.organization.id}`;
        }

        window.open(ssoUrl, "_blank", "noopener,noreferrer");
      } catch (err) {
        console.error("SSO falhou, abrindo link direto:", err);
        window.open(href, "_blank", "noopener,noreferrer");
      } finally {
        setSsoLoadingHref(null);
      }
    },
    [ssoLoadingHref, activeContext],
  );

  // ─── navigation.tsx → seções ──────────────────────────────────────────────
  // Cada divisor abre uma seção nova (o título é o `label`); os itens antes do primeiro divisor
  // formam a seção sem título do topo.
  const sections = useMemo<SidebarSection[]>(() => {
    const failedBadge =
      failedCount > 0
        ? {
            kind: "count" as const,
            value: failedCount,
            label: t("{count} posts com falha", { count: failedCount }),
          }
        : undefined;

    const result: SidebarSection[] = [{ id: "top", items: [] }];
    navigation.forEach((item, idx) => {
      if ("type" in item) {
        result.push({
          id: item.label ?? `section-${idx}`,
          title: item.label ? t(item.label) : undefined,
          collapsible: item.collapsible,
          // Seção recolhível hoje é a dos Apps Virtus: leva a outros produtos
          outbound: item.collapsible,
          items: [],
        });
        return;
      }
      const section = result[result.length - 1];
      const converted: SidebarItem = isNavItemWithChildren(item)
        ? {
            label: t(item.name),
            icon: item.icon,
            tooltip: item.tooltip ? t(item.tooltip) : undefined,
            badge: item.name === POSTS_GROUP ? failedBadge : undefined,
            children: item.children.map((child) => ({
              label: t(child.name),
              href: child.href,
              tooltip: child.tooltip ? t(child.tooltip) : undefined,
              badge:
                child.href === POSTS_HREF
                  ? failedBadge
                  : child.badge
                    ? { kind: "tag", text: child.badge }
                    : undefined,
            })),
          }
        : {
            label: t(item.name),
            href: item.href,
            icon: item.icon,
            tooltip: item.tooltip ? t(item.tooltip) : undefined,
            badge: item.badge ? { kind: "tag", text: item.badge } : undefined,
            external: item.external,
            loading: ssoLoadingHref === item.href,
            onClick: item.external
              ? (e) => handleSSOExternalLink(e, item.href)
              : undefined,
          };
      section.items.push(converted);
    });
    return result.filter((s) => s.items.length > 0);
  }, [t, failedCount, ssoLoadingHref, handleSSOExternalLink]);

  return (
    <VirtusSidebar
      sections={sections}
      footer={[
        { label: t("Ajuda & Suporte"), href: "/support", icon: LifeBuoy },
      ]}
      brand={{
        logoSrc: "/logo.png",
        product: "SMM",
        markSrc: "/favicon.ico",
        homeHref: "/",
        homeLabel: "Virtus SMM — ir para o início",
      }}
      currentPath={location.pathname}
      isActive={isActive}
      renderLink={renderLink}
      renderTooltip={renderTooltip}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      isMobile={isMobile}
      mobileOpen={mobileOpen}
      onCloseMobile={onCloseMobile}
      labels={{
        mainMenu: t("Menu principal"),
        collapse: t("Recolher menu"),
        expand: t("Expandir menu"),
        close: t("Fechar menu"),
        opening: t("Abrindo..."),
        shortcut: "Ctrl+B",
      }}
    />
  );
};

// memo: não re-renderizar a cada tecla da busca do header
export default React.memo(Sidebar);
